import type { ErrorEvent, Event, EventHint, StackFrame } from "@sentry/react";

import type { Environments } from "@/config/env";
import {
  SENTRY_CONFIG,
  SENTRY_ENABLED_ENVIRONMENTS,
  SENTRY_IGNORED_ERRORS,
  SENTRY_IGNORED_STACK_PATTERNS,
  SENTRY_IGNORED_TRANSACTIONS,
  SENTRY_RELEASE,
} from "@/constants/sentry";

type TransactionEvent = Event & { type: "transaction" };

export const isSentryEnabled = (): boolean => {
  const env = import.meta.env.VITE_ENVIRONMENT as Environments;
  return SENTRY_ENABLED_ENVIRONMENTS.includes(env);
};

export const getRelease = (): string => {
  return SENTRY_RELEASE;
};

export const getTracesSampleRate = (): number => {
  const env = import.meta.env.VITE_ENVIRONMENT as Environments;
  return SENTRY_CONFIG.TRACES_SAMPLE_RATE[env];
};

export const getReplaysSampleRates = (): { session: number; error: number } => {
  const env = import.meta.env.VITE_ENVIRONMENT as Environments;
  return {
    session: SENTRY_CONFIG.REPLAYS_SESSION_SAMPLE_RATE[env],
    error: SENTRY_CONFIG.REPLAYS_ERROR_SAMPLE_RATE[env],
  };
};

const hasIgnoredStackTrace = (event: ErrorEvent): boolean => {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames) return false;

  return frames.some((frame: StackFrame) => {
    const filename = frame.filename ?? "";
    return SENTRY_IGNORED_STACK_PATTERNS.some((pattern) => pattern.test(filename));
  });
};

export const beforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  if (hasIgnoredStackTrace(event)) {
    return null;
  }

  const error = hint.originalException;
  const message = error instanceof Error ? error.message : String(error);

  const lowerCaseMessage = message.toLowerCase();

  for (const pattern of SENTRY_IGNORED_ERRORS) {
    if (lowerCaseMessage.includes(pattern.toLowerCase())) {
      return null;
    }
  }

  return event;
};

export const beforeSendTransaction = (
  event: TransactionEvent,
): TransactionEvent | null => {
  const transaction = event.transaction ?? "";
  const isIgnoredTransaction = SENTRY_IGNORED_TRANSACTIONS.some((pattern) =>
    transaction.includes(pattern),
  );

  return isIgnoredTransaction ? null : event;
};
